/**
 * WHAT THE THREE SENTRY RUNTIMES AGREE ON
 *
 * `@sentry/nextjs` initialises three times — browser
 * (`instrumentation-client.ts`), Node server and Edge (both from
 * `instrumentation.ts`) — and the SDK's own scaffolding is three files that
 * each repeat the DSN, the environment and the release. Three copies of a
 * privacy setting is three chances for one of them to be wrong, and the one
 * that would be wrong is the browser's, which is the only one members are
 * subject to. So the shared half lives here.
 *
 * INERT WITHOUT A DSN. `Sentry.init({ dsn: undefined })` does not report, so
 * dev, CI and every test run are silent without needing a flag of their own —
 * the same rule as backend/src/sentry.ts.
 *
 * `NEXT_PUBLIC_` BECAUSE IT IS INLINED AT BUILD TIME, not read at runtime. A
 * DSN set only on the running container reaches the server bundle and NOT the
 * browser bundle, which reports nothing while looking configured. It has to be
 * passed to `next build` — see next-app/Containerfile and redeploy-stage.sh,
 * which both pass it as a build argument alongside NEXT_PUBLIC_BUILD_ID.
 */

export const SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY_DSN || undefined;

/**
 * Stage and prod both build with `NODE_ENV=production`, so that is not an
 * answer to "which deployment produced this". `SENTRY_ENVIRONMENT` is, and
 * defaults to `development` for a local build.
 */
export const SENTRY_ENVIRONMENT =
  process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || "development";

/**
 * The deploy identity already inlined for the footer's build stamp. Reusing it
 * means an event's `release` and the build a member can read off the page are
 * the same string.
 */
export const SENTRY_RELEASE = process.env.NEXT_PUBLIC_BUILD_ID || undefined;

/**
 * Options every runtime gets.
 *
 * `sendDefaultPii: false` IS THE LOAD-BEARING ONE. This app renders members'
 * names, email addresses and private messages. With PII on, the SDK attaches
 * the signed-in user's identity to every event, and — in a replay — the DOM
 * that was on screen. The whole point of the masking below is that an error
 * report tells us WHAT broke without telling us WHO it happened to or what
 * they were reading.
 */
export const sharedOptions = {
  dsn: SENTRY_DSN,
  environment: SENTRY_ENVIRONMENT,
  release: SENTRY_RELEASE,
  sendDefaultPii: false,
  /**
   * Tracing is off, deliberately. Production is ~50 users; there is no latency
   * problem to find and a trace per request is quota spent on graphs nobody
   * has a reason to open. Turning it on is a deliberate act.
   */
  tracesSampleRate: 0,
} as const;
